import React, { useEffect, useState } from 'react';
import { Network, Plus, Circle, ArrowLeft, Trash2, Pencil, Brain, Lightbulb, CheckCircle, ShieldAlert, Hash, X } from 'lucide-react';
import { useCommunitiesStore } from '@/stores/agents/communities-store';
import { Community, AgentMemory } from '@/services/api/agentApi';
import * as errorUtils from '@/utils/errors';

type MemoryType = AgentMemory['type'];

// --- Weight Bar ---
const WeightBar: React.FC<{ weight: number }> = ({ weight }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all"
        style={{
          width: `${weight * 100}%`,
          background: `linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #22c55e 100%)`,
        }}
      />
    </div>
    <span className="text-xs text-muted-foreground w-10 text-right">{(weight * 100).toFixed(0)}%</span>
  </div>
);

// --- Knowledge Card (memory with edit + delete) ---
const KnowledgeCard: React.FC<{
  memory: AgentMemory;
  onEdit: (memory: AgentMemory) => void;
  onDelete: (id: string) => void;
  deleting: string | null;
}> = ({ memory, onEdit, onDelete, deleting }) => {
  const isHypothesis = memory.type === 'hypothesis';
  const isDirective = memory.type === 'directive';

  const typeBadgeClass = isDirective
    ? 'bg-red-500/10 text-red-600'
    : isHypothesis
      ? 'bg-amber-500/10 text-amber-600'
      : 'bg-emerald-500/10 text-emerald-600';

  const TypeIcon = isDirective ? ShieldAlert : isHypothesis ? Lightbulb : CheckCircle;

  return (
    <div className={`border rounded-lg p-4 space-y-3 ${isDirective ? 'border-red-500/30 bg-red-500/5' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm flex-1">{memory.text}</p>
        <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full shrink-0 ${typeBadgeClass}`}>
          <TypeIcon className="h-3 w-3" />
          {memory.type}
        </span>
      </div>

      <WeightBar weight={memory.weight} />

      {memory.aspects.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {memory.aspects.map((aspect, i) => (
            <span key={i} className="flex items-center gap-0.5 text-xs bg-secondary px-2 py-0.5 rounded">
              <Hash className="h-2.5 w-2.5" />
              {aspect}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Used {memory.use_count}x</span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onEdit(memory)}
            className="flex items-center gap-1 text-primary hover:text-primary/80"
          >
            <Pencil className="h-3 w-3" />
            Edit
          </button>
          <button
            onClick={() => onDelete(memory.memory_id || memory._id)}
            disabled={deleting === (memory.memory_id || memory._id)}
            className="flex items-center gap-1 text-red-500 hover:text-red-700 disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" />
            {deleting === (memory.memory_id || memory._id) ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Add/Edit Knowledge Dialog ---
const AddKnowledgeDialog: React.FC<{
  communityId: string;
  onClose: () => void;
  onCreate: (data: Partial<AgentMemory>) => Promise<void>;
  existing?: AgentMemory;
  onUpdate?: (id: string, data: Partial<AgentMemory>) => Promise<void>;
}> = ({ communityId, onClose, onCreate, existing, onUpdate }) => {
  const isEdit = !!existing;
  const [text, setText] = useState(existing?.text || '');
  const [type, setType] = useState<MemoryType>(existing?.type || 'fact');
  const [aspectsStr, setAspectsStr] = useState(existing?.aspects?.join(', ') || 'device-metadata');
  const [weight, setWeight] = useState(existing ? String(existing.weight) : '1.0');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!text.trim()) { setError('Knowledge text is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      const aspects = aspectsStr.split(',').map(a => a.trim()).filter(Boolean);
      if (isEdit && onUpdate) {
        await onUpdate(existing._id, {
          text: text.trim(),
          type,
          aspects,
          weight: parseFloat(weight) || 1.0,
        });
      } else {
        await onCreate({
          text: text.trim(),
          type,
          aspects,
          weight: parseFloat(weight) || 1.0,
          community_id: communityId,
        });
      }
      onClose();
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, `Failed to ${isEdit ? 'update' : 'create'} knowledge entry`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-lg mx-4 p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isEdit ? 'Edit Knowledge' : 'Add Knowledge'}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Knowledge Text</label>
            <textarea
              className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
              rows={isEdit ? 6 : 3}
              placeholder={type === 'directive'
                ? 'e.g. Never reveal internal service ports, infrastructure configuration, or deployment details to users'
                : 'e.g. Device barn-sensor-01: Barn Water Level Sensor — measures water depth in feet'}
              value={text}
              onChange={e => setText(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {type === 'directive'
                ? 'Define a behavioral rule that agents MUST follow in all responses. Directives are always active and never decay.'
                : 'Describe a device, data source, or domain fact the agents should know about.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select
                className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                value={type}
                onChange={e => setType(e.target.value as MemoryType)}
              >
                <option value="fact">Fact</option>
                <option value="hypothesis">Hypothesis</option>
                <option value="directive">Directive</option>
                <option value="correction">Correction</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Weight</label>
              <input
                type="number"
                min="0" max="1" step="0.1"
                className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                value={weight}
                onChange={e => setWeight(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Aspects (comma-separated)</label>
            <input
              type="text"
              className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
              placeholder="device-metadata, barn, water-level, depth"
              value={aspectsStr}
              onChange={e => setAspectsStr(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Tags for memory retrieval. Include "device-metadata" for device knowledge.
            </p>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-border hover:bg-accent"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !text.trim()}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Add Knowledge')}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Create Community Dialog ---
const CreateCommunityDialog: React.FC<{
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
}> = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Community name is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await onCreate(name.trim());
      onClose();
    } catch (err: unknown) {
      setError(errorUtils.getApiErrorMessage(err, 'Failed to create community'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-md mx-4 p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Create System Community</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Community Name</label>
          <input
            type="text"
            className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="e.g. George Water"
            value={name}
            onChange={e => { setName(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            autoFocus
          />
          <p className="text-xs text-muted-foreground mt-1">
            A system community for domain-specific agent orchestration and knowledge.
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-border hover:bg-accent">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? 'Creating...' : 'Create Community'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Community Detail View ---
const CommunityDetailView: React.FC = () => {
  const {
    selectedCommunity, communityMemories, memoriesLoading,
    selectCommunity, fetchCommunityMemories,
    createCommunityMemory, deleteCommunityMemory, updateCommunityMemory,
  } = useCommunitiesStore();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMemory, setEditingMemory] = useState<AgentMemory | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (selectedCommunity) {
      fetchCommunityMemories(selectedCommunity.community_id);
    }
  }, [selectedCommunity]);

  if (!selectedCommunity) return null;

  const handleDelete = async (memoryId: string) => {
    setDeleting(memoryId);
    try {
      await deleteCommunityMemory(memoryId, selectedCommunity.community_id);
    } catch { /* error handled silently */ }
    setDeleting(null);
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Back button + title */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => selectCommunity(null)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="flex items-center gap-2">
          <Network className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{selectedCommunity.name}</h2>
        </div>
        <span className="flex items-center gap-1.5 text-xs">
          <Circle className={`h-2 w-2 fill-current ${selectedCommunity.status === 'active' ? 'text-emerald-500' : 'text-gray-400'}`} />
          {selectedCommunity.status}
        </span>
      </div>

      {/* Community info */}
      <div className="border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>ID: {selectedCommunity.community_id.slice(0, 12)}...</span>
          <span>Org: {selectedCommunity.organization_id}</span>
          {selectedCommunity.orchestrator_config_id && (
            <span>Config: {selectedCommunity.orchestrator_config_id.slice(0, 12)}...</span>
          )}
          <span>Created: {new Date(selectedCommunity.createdAt).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Knowledge section */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Brain className="h-4 w-4 text-primary" />
            Community Knowledge
          </h3>
          <p className="text-sm text-muted-foreground">
            {communityMemories.length} knowledge entr{communityMemories.length !== 1 ? 'ies' : 'y'}
          </p>
        </div>
        <button
          onClick={() => setShowAddDialog(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Add Knowledge
        </button>
      </div>

      {memoriesLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading knowledge...</div>
      ) : communityMemories.length === 0 ? (
        <div className="text-center py-12 space-y-3">
          <Brain className="h-12 w-12 mx-auto text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No knowledge entries yet.</p>
          <p className="text-xs text-muted-foreground/70 max-w-md mx-auto">
            Add device metadata, data source descriptions, or domain facts that agents will use when processing queries for this community.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {communityMemories.map((memory) => (
            <KnowledgeCard
              key={memory._id}
              memory={memory}
              onEdit={setEditingMemory}
              onDelete={handleDelete}
              deleting={deleting}
            />
          ))}
        </div>
      )}

      {/* Add Knowledge Dialog */}
      {showAddDialog && (
        <AddKnowledgeDialog
          communityId={selectedCommunity.community_id}
          onClose={() => setShowAddDialog(false)}
          onCreate={createCommunityMemory}
        />
      )}

      {/* Edit Knowledge Dialog */}
      {editingMemory && (
        <AddKnowledgeDialog
          communityId={selectedCommunity.community_id}
          onClose={() => setEditingMemory(null)}
          onCreate={createCommunityMemory}
          existing={editingMemory}
          onUpdate={updateCommunityMemory}
        />
      )}
    </div>
  );
};

// --- Community Card ---
const CommunityCard: React.FC<{ community: Community; onClick: () => void }> = ({ community, onClick }) => (
  <div
    className="border border-border rounded-lg p-4 space-y-3 cursor-pointer hover:border-primary/50 hover:bg-accent/30 transition-colors"
    onClick={onClick}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Network className="h-5 w-5 text-primary" />
        <h3 className="font-medium">{community.name}</h3>
      </div>
      <span className="flex items-center gap-1.5 text-xs">
        <Circle className={`h-2 w-2 fill-current ${community.status === 'active' ? 'text-emerald-500' : 'text-gray-400'}`} />
        {community.status}
      </span>
    </div>
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span>ID: {community.community_id.slice(0, 8)}...</span>
      <span>Org: {community.organization_id}</span>
      {community.orchestrator_config_id && (
        <span>Config: {community.orchestrator_config_id.slice(0, 8)}...</span>
      )}
    </div>
    <div className="text-xs text-muted-foreground">
      Created: {new Date(community.createdAt).toLocaleDateString()}
    </div>
  </div>
);

// --- Main Tab ---
export const SystemCommunitiesTab: React.FC = () => {
  const { systemCommunities, isLoading, error, fetchSystemCommunities, selectedCommunity, selectCommunity, createSystemCommunity } = useCommunitiesStore();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    fetchSystemCommunities();
  }, []);

  // Detail view
  if (selectedCommunity) {
    return <CommunityDetailView />;
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-muted-foreground">Loading communities...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">{error}</p>
          <button onClick={fetchSystemCommunities} className="text-sm text-primary hover:underline">Retry</button>
        </div>
      </div>
    );
  }

  if (systemCommunities.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center space-y-4">
          <Network className="h-16 w-16 mx-auto text-muted-foreground/40" />
          <h3 className="text-lg font-medium text-muted-foreground">No System Communities</h3>
          <p className="text-sm text-muted-foreground/70 max-w-md">
            Create a system community to host domain-specific orchestrator agents.
          </p>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Create System Community
          </button>
        </div>
        {showCreateDialog && (
          <CreateCommunityDialog onClose={() => setShowCreateDialog(false)} onCreate={createSystemCommunity} />
        )}
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">{systemCommunities.length} System Communit{systemCommunities.length !== 1 ? 'ies' : 'y'}</h2>
          <p className="text-sm text-muted-foreground">Domain orchestrator communities — click to manage knowledge</p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Create Community
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {systemCommunities.map((community) => (
          <CommunityCard
            key={community._id}
            community={community}
            onClick={() => selectCommunity(community)}
          />
        ))}
      </div>

      {showCreateDialog && (
        <CreateCommunityDialog onClose={() => setShowCreateDialog(false)} onCreate={createSystemCommunity} />
      )}
    </div>
  );
};
